"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { RiCheckboxCircleLine } from "react-icons/ri";
import { ActionInput, Button, CardOption, EmptyState, HintCard } from "@dionatanbig2be/big2be-ui";
import { removeNonNumbers } from "@dionatanbig2be/big2be-ui/utils";
import { OtpInput } from "@/shared/components/OtpInput";
import { toast } from "@/shared/components";
import { getApiErrorMessage } from "@/shared/helpers/errors";
import { codeResendHoldSeconds } from "../lib/code-limit";
import { useAuthFormFields } from "@/shell/hooks/useBootstrap";
import { AuthField } from "./AuthField";
import { useAuth } from "../hooks/useAuth";
import { useAuthModal } from "../stores/useAuthModal";
import {
    NEW_PASSWORD_MIN_LENGTH,
    RECOVERY_CHANNELS,
    RECOVERY_CODE_LENGTH,
    RESEND_COOLDOWN_SECONDS,
    recoveryChannelCopy,
} from "../lib/recovery-channels";
import {
    loginIdentifierInput,
    loginIdentifiers,
    resolveLoginIdentifier,
} from "../lib/auth-fields";
import {
    getRecoveryChannels,
    requestRecoveryCode,
    resetPassword,
    verifyRecoveryCode,
} from "../services/password-recovery.service";
import type { Resolver } from "react-hook-form";
import type { RecoveryChannel, RecoveryIdentifier } from "../types/password-recovery.dto";

/** Passos do fluxo: método+identificador → código → nova senha → fim. */
type Step = "identifier" | "code" | "password" | "success";

interface IdentifierFormValues {
    cpfCnpj?: string;
    email?: string;
    phone?: string;
    identifier?: string;
}

/**
 * "Esqueci minha senha" — o fluxo inteiro dentro do modal de auth.
 *
 * Os campos de identificação são os MESMOS do login (`login_form_fields` do
 * cliente, via `loginIdentifierInput`): se a loja só aceita CPF, é CPF que se
 * pede aqui também — e o tipo digitado é resolvido no front
 * (`resolveLoginIdentifier`), então o back recebe o campo certo em vez de
 * adivinhar entre CPF e celular.
 *
 * O identificador já vem preenchido com o que o cliente digitou no login (ver
 * `recoveryPrefill` na store do modal) — CPF, e-mail ou telefone, tanto faz.
 *
 * Os métodos de envio vêm de `GET /auth/password/channels`: só aparece o que a
 * loja consegue enviar de fato. Sem nenhum canal disponível, o fluxo avisa em
 * vez de oferecer um botão que falharia.
 *
 * Envio é operação que CUSTA (mensagem paga, e o back tem janela de 30s entre
 * dois códigos para o mesmo destino), então todo botão que dispara request é
 * bloqueado enquanto a anterior não termina, e o reenvio só volta quando o
 * contador zera.
 */
interface ForgotPasswordFormProps {
    /** Senha trocada E login feito — o modal fecha (e honra o `?redirect=`). */
    onAuthenticated: () => void;
    /** Senha trocada, mas o login automático falhou: volta pro login. */
    onDone: () => void;
}

export function ForgotPasswordForm({ onAuthenticated, onDone }: ForgotPasswordFormProps) {
    const config = useAuthFormFields().login;
    const { login } = useAuth();

    const identifierField = useMemo(() => loginIdentifierInput(config), [config]);
    const identifiers = useMemo(() => loginIdentifiers(config), [config]);
    const campoUnico = identifierField?.name === "identifier";

    const schema = useMemo(
        () =>
            yup.object(
                identifierField ? { [identifierField.name]: identifierField.schema(true) } : {}
            ),
        [identifierField]
    );

    // O que o cliente digitou no login (ver `LoginForm`). Só entra no campo que
    // a loja realmente exibe — config com um identificador só não tem "identifier".
    const prefill = useAuthModal((state) => state.recoveryPrefill);
    const defaultValues = useMemo<IdentifierFormValues>(() => {
        if (!prefill || !identifierField) return {};
        const digitado =
            prefill.identifier || prefill.cpfCnpj || prefill.email || prefill.phone || "";
        return digitado ? { [identifierField.name]: digitado } : {};
    }, [prefill, identifierField]);

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<IdentifierFormValues>({
        resolver: yupResolver(schema) as unknown as Resolver<IdentifierFormValues>,
        defaultValues,
    });

    const [step, setStep] = useState<Step>("identifier");
    const [availableChannels, setAvailableChannels] = useState<RecoveryChannel[] | null>(null);
    /** Falha ao LER os métodos — diferente de "a loja não tem nenhum". */
    const [channelsFailed, setChannelsFailed] = useState(false);
    const [reloadToken, setReloadToken] = useState(0);
    const [channel, setChannel] = useState<RecoveryChannel | null>(null);
    const [identifierPayload, setIdentifierPayload] = useState<RecoveryIdentifier | null>(null);
    const [destination, setDestination] = useState("");
    const [code, setCode] = useState<string[]>(Array(RECOVERY_CODE_LENGTH).fill(""));
    const [resetToken, setResetToken] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    /** Segundos restantes até poder reenviar (0 = liberado). */
    const [secondsLeft, setSecondsLeft] = useState(0);

    // Métodos disponíveis: uma leitura só, na montagem. O primeiro disponível
    // já vem selecionado — quando só existe um, o cliente não escolhe nada.
    useEffect(() => {
        // `active` evita escrever estado depois de desmontar (o modal fecha no
        // meio da leitura com facilidade).
        let active = true;

        void (async () => {
            try {
                const { channels } = await getRecoveryChannels();
                if (!active) return;
                const enabled = channels
                    .filter((item) => item.available)
                    .map((item) => item.channel);
                setAvailableChannels(enabled);
                setChannel((current) => current ?? enabled[0] ?? null);
                setChannelsFailed(false);
            } catch {
                if (!active) return;
                setAvailableChannels([]);
                setChannelsFailed(true);
            }
        })();

        return () => {
            active = false;
        };
        // `reloadToken` muda no "tentar novamente" e refaz a leitura.
    }, [reloadToken]);

    // Contagem do reenvio: um `setTimeout` por segundo, disparado pela própria
    // mudança de estado — sem `setInterval` para limpar nem setState síncrono
    // dentro do efeito.
    useEffect(() => {
        if (secondsLeft <= 0) return;
        const timer = setTimeout(() => setSecondsLeft((current) => current - 1), 1000);
        return () => clearTimeout(timer);
    }, [secondsLeft]);

    /** Botão "tentar novamente": volta ao estado de carregando e relê. */
    function retryChannels() {
        setAvailableChannels(null);
        setChannelsFailed(false);
        setReloadToken((token) => token + 1);
    }

    /** Opções na ordem da copy, filtradas pelo que o back consegue enviar. */
    const options = useMemo(
        () => RECOVERY_CHANNELS.filter((item) => availableChannels?.includes(item.channel)),
        [availableChannels]
    );

    const sendCode = useCallback(
        async (payload: RecoveryIdentifier, selected: RecoveryChannel) => {
            const result = await requestRecoveryCode({ ...payload, channel: selected });
            setDestination(result.destination);
            setCode(Array(RECOVERY_CODE_LENGTH).fill(""));
            setSecondsLeft(RESEND_COOLDOWN_SECONDS);
            setStep("code");
        },
        []
    );

    async function onSubmitIdentifier(values: IdentifierFormValues) {
        // Guarda de duplo clique: o `disabled` do botão cobre o clique do mouse,
        // mas Enter no campo dispara o submit de novo antes do estado atualizar.
        if (busy || !channel) return;

        // Campo único: o tipo sai do conteúdo digitado. Campo próprio: o valor
        // já está na chave certa.
        const resolved = campoUnico
            ? resolveLoginIdentifier(values.identifier, identifiers)
            : values;

        const payload: RecoveryIdentifier = {};
        if (resolved?.cpfCnpj) payload.cpfCnpj = removeNonNumbers(resolved.cpfCnpj);
        if (resolved?.email) payload.email = resolved.email;
        if (resolved?.phone) payload.phone = removeNonNumbers(resolved.phone);

        if (!payload.cpfCnpj && !payload.email && !payload.phone) {
            toast.error("Informe um dado válido para localizarmos sua conta.");
            return;
        }

        setBusy(true);
        try {
            setIdentifierPayload(payload);
            await sendCode(payload, channel);
        } catch (error) {
            toast.error(
                getApiErrorMessage(error, "Não foi possível enviar o código. Tente novamente.")
            );
        } finally {
            setBusy(false);
        }
    }

    async function handleResend() {
        if (busy || secondsLeft > 0 || !identifierPayload || !channel) return;

        setBusy(true);
        try {
            await sendCode(identifierPayload, channel);
            toast("Enviamos um novo código.");
        } catch (error) {
            toast.error(getApiErrorMessage(error, "Não foi possível reenviar o código."));
            // 429 (5 códigos por hora): trava o reenviar — clicar de novo só repete o erro.
            const hold = codeResendHoldSeconds(error);
            if (hold > 0) setSecondsLeft(hold);
        } finally {
            setBusy(false);
        }
    }

    async function handleVerify() {
        if (busy || !identifierPayload) return;

        setBusy(true);
        try {
            const result = await verifyRecoveryCode({
                ...identifierPayload,
                code: code.join(""),
            });
            setResetToken(result.resetToken);
            setStep("password");
        } catch (error) {
            toast.error(getApiErrorMessage(error, "Código inválido. Confira e tente novamente."));
        } finally {
            setBusy(false);
        }
    }

    async function handleReset() {
        if (busy) return;
        setBusy(true);
        try {
            await resetPassword({ resetToken, newPassword: password });
        } catch (error) {
            toast.error(
                getApiErrorMessage(error, "Não foi possível redefinir sua senha. Tente novamente.")
            );
            setBusy(false);
            return;
        }

        // Senha trocada: entra direto. O cliente acabou de provar posse do
        // contato (código) e escolheu a senha — pedir login de novo é atrito sem
        // ganho de segurança. Vai pelo caminho normal de login (BFF + cookies
        // httpOnly), então nenhum token passa pelo JavaScript.
        try {
            await login({ ...identifierPayload, password });
            toast("Senha redefinida. Você já está logado.");
            onAuthenticated();
        } catch {
            // A troca deu certo; só a entrada automática falhou (ex.: a loja
            // exige outro identificador no login). Não é erro pro cliente.
            toast("Senha redefinida. Faça login para continuar.");
            setStep("success");
        } finally {
            setBusy(false);
        }
    }

    const codeFilled = code.every((digit) => digit !== "");
    const longEnough = password.length >= NEW_PASSWORD_MIN_LENGTH;
    const passwordValid = longEnough && password === confirm;
    const copy = channel ? recoveryChannelCopy(channel) : null;

    //SECTION - Passo 4: sucesso
    if (step === "success") {
        return (
            <EmptyState
                icon={<RiCheckboxCircleLine size={56} aria-hidden="true" />}
                iconClassName="text-success"
                title="Senha redefinida!"
                description="Sua nova senha já está ativa. Use-a para acessar sua conta."
                action={
                    <Button type="button" tone="primary" className="mt-3 min-w-40" onClick={onDone}>
                        Fazer login
                    </Button>
                }
            />
        );
    }
    //!SECTION

    //SECTION - Passo 3: nova senha
    if (step === "password") {
        return (
            <form
                noValidate
                className="flex flex-col gap-4"
                onSubmit={(event) => {
                    event.preventDefault();
                    void handleReset();
                }}
            >
                <div className="flex flex-col gap-2">
                    <ActionInput
                        action="secret"
                        label="Nova senha"
                        placeholder="Senha"
                        autoComplete="new-password"
                        disabled={busy}
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                    />
                    <HintCard
                        tone={longEnough ? "success" : "info"}
                        title={longEnough ? "Senha válida" : "Requisito da senha"}
                        description={`Mínimo de ${NEW_PASSWORD_MIN_LENGTH} caracteres.`}
                    />
                </div>

                <ActionInput
                    action="secret"
                    label="Confirme a nova senha"
                    placeholder="Confirme sua senha"
                    autoComplete="new-password"
                    disabled={busy}
                    error={confirm && password !== confirm ? "As senhas não conferem" : undefined}
                    value={confirm}
                    onChange={(event) => setConfirm(event.target.value)}
                />

                <Button
                    type="submit"
                    tone="primary"
                    width="block"
                    isLoading={busy}
                    disabled={busy || !passwordValid}
                >
                    Salvar nova senha
                </Button>
            </form>
        );
    }
    //!SECTION

    //SECTION - Passo 2: código
    if (step === "code") {
        return (
            <div className="flex flex-col gap-5">
                {/* Destino em destaque: é a confirmação de que o código foi para
                    o contato certo — e o cliente costuma precisar dele à vista
                    enquanto troca de app para buscar o código. */}
                <div className="flex flex-col gap-1 rounded-box bg-base-200 px-3 py-2.5 text-center">
                    <span className="text-xs uppercase tracking-wide opacity-60">
                        Código enviado para
                    </span>
                    <span className="truncate text-sm font-semibold">{destination}</span>
                </div>

                <p className="text-center text-sm leading-relaxed opacity-70">
                    {copy?.sentTo(destination)} Insira os {RECOVERY_CODE_LENGTH} dígitos para
                    continuar.
                </p>

                <OtpInput value={code} onChange={setCode} length={RECOVERY_CODE_LENGTH} />

                <Button
                    type="button"
                    tone="primary"
                    width="block"
                    isLoading={busy}
                    disabled={busy || !codeFilled}
                    onClick={() => void handleVerify()}
                >
                    Continuar
                </Button>

                <p className="text-center text-xs opacity-60">
                    {secondsLeft > 0 ? (
                        <>Ainda não recebeu? Você pode reenviar em {secondsLeft}s.</>
                    ) : (
                        <>
                            Ainda não recebeu?{" "}
                            <Button
                                type="button"
                                variant="link"
                                size="xs"
                                className="align-baseline"
                                disabled={busy}
                                onClick={() => void handleResend()}
                            >
                                {copy?.resend}
                            </Button>
                        </>
                    )}
                </p>
            </div>
        );
    }
    //!SECTION

    //SECTION - Passo 1: método de envio + identificador
    return (
        <form
            noValidate
            onSubmit={handleSubmit(onSubmitIdentifier)}
            className="grid grid-cols-2 gap-3"
        >
            {availableChannels === null ? (
                <p className="col-span-2 text-sm opacity-60">Carregando métodos de envio…</p>
            ) : channelsFailed ? (
                <div className="col-span-2 flex flex-col gap-3">
                    <HintCard
                        tone="error"
                        title="Não foi possível carregar os métodos"
                        description="Verifique sua conexão e tente novamente."
                    />
                    <Button type="button" tone="primary" onClick={retryChannels}>
                        Tentar novamente
                    </Button>
                </div>
            ) : options.length === 0 ? (
                <div className="col-span-2">
                    <HintCard
                        tone="warning"
                        title="Recuperação indisponível"
                        description="Esta loja ainda não tem um método de envio de código configurado. Entre em contato com o atendimento para redefinir sua senha."
                    />
                </div>
            ) : (
                <>
                    {/* Um único método disponível não é escolha — só informa por
                        onde o código vai. */}
                    {options.length > 1 && (
                        <fieldset className="col-span-2 m-0 flex flex-col gap-2 border-0 p-0">
                            <legend className="mb-1 text-sm font-medium">
                                Como você quer receber o código?
                            </legend>
                            {options.map((option) => (
                                <CardOption
                                    key={option.channel}
                                    selected={channel === option.channel}
                                    onClick={() => setChannel(option.channel)}
                                    icon={<option.Icon size={18} aria-hidden="true" />}
                                    label={option.label}
                                    showIndicator
                                    indicatorType="radio"
                                />
                            ))}
                        </fieldset>
                    )}

                    <p className="col-span-2 text-sm leading-relaxed opacity-70">
                        {options.length === 1 && copy
                            ? `Informe seus dados e enviaremos um código de verificação por ${copy.label
                                  .replace("Receber código por ", "")
                                  .trim()}.`
                            : "Informe seus dados e enviaremos um código de verificação."}
                    </p>

                    {identifierField && (
                        <AuthField
                            def={identifierField}
                            control={control}
                            errors={errors}
                            disabled={busy}
                        />
                    )}

                    <Button
                        type="submit"
                        tone="primary"
                        className="col-span-2"
                        isLoading={busy}
                        disabled={busy || !channel}
                    >
                        Enviar código
                    </Button>
                </>
            )}
        </form>
    );
    //!SECTION
}
